/**
 * FO-310 (batch-34) — Gereedschapskarren & koffers: dragers met lades, lay-in
 * modules (artikel mét inhoud), templates, controleflow, bestelvoorstel, portal.
 *
 * Een drager is een `VoorraadUniekItem`-exemplaar (QR, historie, uitgifte gratis
 * erbij) met een 1:1 `GereedschapsDrager`-satelliet. De inhoud leeft per lade
 * (`DragerLade` → `DragerInhoudRegel`); de FO-301-koffer-inhoud gaat hier via het
 * omzet-script in op. Inhoud = aantallen; alleen de drager is traceerbaar.
 *
 * Conventies (gelijk aan koffer.ts / de rest van packages/types):
 *   - ID-referenties in Zod: `z.string().min(1)` (NIET `.cuid()` — prod-legacy-UUID).
 *   - Optioneel-nullable: `.optional().nullable()`.
 *   - Geen kostvelden in de portal-DTO's (geen inkoop-/verkoopprijs naar de monteur).
 */
import { z } from 'zod'
import { fotoBronSchema } from './voorraad'

// ─── Enums (as-const + type + labels) ────────────────────────────────────────

/** Nummer-reeks per soort drager (bepaalt het assetTag-prefix). */
// batch-69 samenstellen (FO-510): PBM = PBM-koffer als complete set (PBM1001+).
export const DRAGER_REEKS = ['KAR', 'KOF', 'SK', 'PBM'] as const
export type DragerReeks = (typeof DRAGER_REEKS)[number]
export const DRAGER_REEKS_LABELS: Record<DragerReeks, string> = {
  KAR: 'Gereedschapskar',
  KOF: 'Gereedschapskoffer',
  SK: 'Servicekoffer',
  PBM: 'PBM-koffer',
}

/** batch-69 samenstellen (FO-510 §B): set-stand van een koffer die als complete set op voorraad ligt. */
export const DRAGER_SET_STATUSSEN = ['IN_SAMENSTELLING', 'COMPLEET'] as const
export type DragerSetStatus = (typeof DRAGER_SET_STATUSSEN)[number]
export const DRAGER_SET_STATUS_LABELS: Record<DragerSetStatus, string> = {
  IN_SAMENSTELLING: 'In samenstelling',
  COMPLEET: 'Set compleet',
}
/** Reeksen die via de generieke aanmaak-flow te kiezen zijn (SK blijft FO-301). */
export const DRAGER_REEKS_AANMAAK = ['KAR', 'KOF'] as const
export type DragerReeksAanmaak = (typeof DRAGER_REEKS_AANMAAK)[number]

/** Levenscyclus (handmatig). Los van compleetheid. */
export const DRAGER_LEVENSFASE = [
  'OP_VOORRAAD',
  'IN_GEBRUIK',
  'IN_CONTROLE',
  'BUITEN_GEBRUIK',
  'GEARCHIVEERD',
] as const
export type DragerLevensfase = (typeof DRAGER_LEVENSFASE)[number]
export const DRAGER_LEVENSFASE_LABELS: Record<DragerLevensfase, string> = {
  OP_VOORRAAD: 'Op voorraad',
  IN_GEBRUIK: 'In gebruik',
  IN_CONTROLE: 'In controle',
  BUITEN_GEBRUIK: 'Buiten gebruik',
  GEARCHIVEERD: 'Gearchiveerd',
}
/** Fases waarin geen controle-signaal meer hoeft (kapot/afgeschreven). */
export const DRAGER_LEVENSFASE_INACTIEF: readonly DragerLevensfase[] = [
  'BUITEN_GEBRUIK',
  'GEARCHIVEERD',
]

/** Controlefrequentie → afgeleide vervaldatum. */
export const DRAGER_CONTROLE_FREQUENTIE = [
  'MAANDELIJKS',
  'KWARTAAL',
  'HALFJAAR',
  'JAARLIJKS',
  'BIJ_UITGIFTE',
  'BIJ_INNAME',
  'VOOR_NA_PROJECT',
  'GEEN',
] as const
export type DragerControleFrequentie = (typeof DRAGER_CONTROLE_FREQUENTIE)[number]
export const DRAGER_CONTROLE_FREQUENTIE_LABELS: Record<DragerControleFrequentie, string> = {
  MAANDELIJKS: 'Maandelijks',
  KWARTAAL: 'Per kwartaal',
  HALFJAAR: 'Halfjaarlijks',
  JAARLIJKS: 'Jaarlijks',
  BIJ_UITGIFTE: 'Bij uitgifte',
  BIJ_INNAME: 'Bij inname',
  VOOR_NA_PROJECT: 'Voor/na project',
  GEEN: 'Geen',
}

/** Status van een inhoud-regel + controle-regel. */
export const DRAGER_INHOUD_STATUS = [
  'AANWEZIG',
  'ONTBREEKT',
  'KAPOT',
  'VERSLETEN',
  'KWIJT',
  'IN_BESTELLING',
  'VERVANGEN',
  'NVT',
  // batch-70 uitrusting-controle (FO-531): nooit gekregen (gemeld in de uitrustingscontrole).
  'NIET_ONTVANGEN',
] as const
export type DragerInhoudStatus = (typeof DRAGER_INHOUD_STATUS)[number]
export const DRAGER_INHOUD_STATUS_LABELS: Record<DragerInhoudStatus, string> = {
  AANWEZIG: 'Aanwezig',
  ONTBREEKT: 'Ontbreekt',
  KAPOT: 'Kapot',
  VERSLETEN: 'Versleten',
  KWIJT: 'Kwijt',
  IN_BESTELLING: 'In bestelling',
  VERVANGEN: 'Vervangen',
  NVT: 'N.v.t.',
  NIET_ONTVANGEN: 'Niet ontvangen',
}
/** Statussen die om een bestel-/afboek-beslissing vragen (voeden het bestelvoorstel). */
// batch-70 uitrusting-controle (FO-531): NIET_ONTVANGEN is ook een afwijking (uitgeven of bestellen).
export const DRAGER_STATUS_AFWIJKEND = ['ONTBREEKT', 'KAPOT', 'VERSLETEN', 'KWIJT', 'NIET_ONTVANGEN'] as const
export type DragerStatusAfwijkend = (typeof DRAGER_STATUS_AFWIJKEND)[number]

/** Levenscyclus van een controle. */
export const DRAGER_CONTROLE_STATUS = ['BEZIG', 'AFGEROND', 'GEANNULEERD'] as const
export type DragerControleStatus = (typeof DRAGER_CONTROLE_STATUS)[number]
export const DRAGER_CONTROLE_STATUS_LABELS: Record<DragerControleStatus, string> = {
  BEZIG: 'Bezig',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

/** Gekozen actie per afwijkende regel in het bestelvoorstel. */
export const DRAGER_BESTEL_ACTIE = ['BESTEL_MODULE', 'BESTEL_LOS', 'UIT_VOORRAAD', 'AFBOEKEN'] as const
export type DragerBestelActie = (typeof DRAGER_BESTEL_ACTIE)[number]
export const DRAGER_BESTEL_ACTIE_LABELS: Record<DragerBestelActie, string> = {
  BESTEL_MODULE: 'Complete module bestellen',
  BESTEL_LOS: 'Los onderdeel bestellen',
  UIT_VOORRAAD: 'Uit voorraad leveren',
  AFBOEKEN: 'Afboeken (niet bestellen)',
}

/** Beheerbare typelijst-suggesties (dragerType = vrije tekst, deze zijn de defaults). */
export const DRAGER_TYPE_SUGGESTIES = [
  'Gereedschapskar',
  'Onderhoudskar',
  'Projectkar',
  'Gereedschapskoffer',
  'Servicekoffer',
  'Anders',
] as const

/** Soort inhoud-regel (afgeleid uit welke referentie gevuld is). */
export type DragerInhoudSoort = 'MODULE' | 'LOS_ARTIKEL' | 'LOS_UNIEK'

// ─── Module-catalogus (lay-in modules) ───────────────────────────────────────

/** Eén onderdeel binnen een lay-in module (omschrijving + aantal). */
export interface ModuleOnderdeelDTO {
  id: string
  omschrijving: string
  aantal: number
  /** Optionele koppeling naar een los voorraad-artikel voor los nabestellen. */
  losArtikelId: string | null
  losArtikelNaam: string | null
}

/** Een lay-in module in de catalogus (LAYIN_MODULE-artikel + inhoud). */
export interface ModuleCatalogusDTO {
  id: string
  artikelNummer: string
  naam: string
  moduleMerk: string | null
  moduleCode: string | null
  categorie: string
  standaardLade: number | null
  leverancierNaam: string | null
  /** Inkoopprijs (beheer/backoffice; nooit naar portal). */
  prijs: number | null
  fotoUrl: string | null
  actief: boolean
  inhoud: ModuleOnderdeelDTO[]
  /** Aantal drager-regels dat deze module gebruikt (voor "deactiveren i.p.v. verwijderen"). */
  aantalInGebruik: number
}

// ─── Drager: overzicht + detail ──────────────────────────────────────────────

/** Verantwoordelijke-samenvatting (wie/waar) voor het overzicht. */
export interface DragerKoppelingDTO {
  employeeId: string | null
  employeeNaam: string | null
  voertuigId: string | null
  voertuigKenteken: string | null
  projectId: string | null
  projectNaam: string | null
  locatieId: string | null
  locatieNaam: string | null
}

/** Eén rij in het karren/koffers-overzicht. */
export interface DragerListRowDTO {
  id: string
  uniekItemId: string
  nummer: string | null
  naam: string
  reeks: DragerReeks
  dragerType: string
  levensfase: DragerLevensfase
  /** Afgeleide compleetheid 0-100; null = nog nooit gecontroleerd. */
  compleetheidPct: number | null
  aantalOntbrekend: number
  aantalKapot: number
  aantalOpenBestellingen: number
  koppeling: DragerKoppelingDTO
  laatsteControleOp: string | null
  volgendeControle: string | null
  /** true wanneer volgendeControle in het verleden ligt (controle over tijd). */
  controleOverTijd: boolean
}

/** Eén inhoud-regel binnen een lade (backoffice). */
export interface DragerInhoudRegelDTO {
  id: string
  ladeId: string
  soort: DragerInhoudSoort
  moduleArtikelId: string | null
  losArtikelId: string | null
  losUniekItemId: string | null
  /** Geresolveerde weergavenaam (module-naam / artikel-naam). */
  naam: string
  /** Modulecode (bij een module-regel), anders null. */
  code: string | null
  categorie: string | null
  maat: string | null
  aantal: number
  status: DragerInhoudStatus
  retourplichtig: boolean
  opmerking: string | null
  volgorde: number
  /** Bij een module-regel: de uitklapbare module-inhoud. */
  moduleInhoud: ModuleOnderdeelDTO[]
  // FO-321 — het gekoppelde gereedschap-catalogus-item (via de voorraad-bron), of
  // null. Voedt "bestel vervanging": is er een gekoppeld én bestelbaar item, dan
  // springt de bestelactie ernaartoe; anders val terug op de losse aanvraag.
  catalogusArtikelId: string | null
  /** Effectief bestelbaar (catalogus-vlag ÉN actieve voorraad-bron). */
  catalogusBestelbaar: boolean
}

/** Eén lade met zijn inhoud. */
export interface DragerLadeDTO {
  id: string
  nummer: number
  naam: string | null
  fotoPath: string | null
  opmerking: string | null
  regels: DragerInhoudRegelDTO[]
}

/** Volledig drager-detail (backoffice-kar-detail). */
export interface DragerDetailDTO {
  id: string
  uniekItemId: string
  qrToken: string | null
  nummer: string | null
  naam: string
  reeks: DragerReeks
  dragerType: string
  aantalLades: number
  levensfase: DragerLevensfase
  controleFrequentie: DragerControleFrequentie
  volgendeControle: string | null
  laatsteControleOp: string | null
  compleetheidPct: number | null
  koppeling: DragerKoppelingDTO
  fotoVoorkantPath: string | null
  fotoOpenPath: string | null
  opmerking: string | null
  templateId: string | null
  templateNaam: string | null
  /** Koffer/kar uit pakket (2026-08): bron-pakket waaruit de inhoud is overgenomen. */
  pakketId: string | null
  pakketNaam: string | null
  lades: DragerLadeDTO[]
  /** Aantal regels dat op IN_BESTELLING staat. */
  aantalOpenBestellingen: number
  /** Id van een lopende (BEZIG) controle, of null. */
  lopendeControleId: string | null
}

// ─── FO-320 — drager-kostenbeeld (Kosten-blok, kosten-gate) ──────────────────
//
// Vervangings-/reparatiekosten uit inkoop-toewijzingen met deze drager als doel,
// gekoppeld aan de controle die ze veroorzaakte (besteltraject). Bewust een APARTE
// DTO/endpoint (niet op DragerDetailDTO): bedragen zitten achter `canViewCostPrices`.
// Geen kostvelden in de portal-DTO's (blijft ongewijzigd).

/** Bron-badge van een drager-kostenpost (spiegelt KostenToewijzingBron). */
export interface DragerKostenPostDTO {
  id: string
  /** Datum (yyyy-MM-dd, Europe/Amsterdam). */
  datum: string
  /** Kostensoort-code uit de stamtabel (batch-65 FO-482). */
  soort: string
  soortLabel: string
  omschrijving: string
  /** Bedrag (excl. btw); negatief bij creditnota. */
  bedrag: number
  bron: string
  bronLabel: string
  /** Doorklik-doel: de bron-factuur. */
  factuurId: string | null
  factuurNr: string | null
  /** De controle die de kost veroorzaakte (besteltraject), indien herleidbaar. */
  controleId: string | null
  controleDatum: string | null
}

export interface DragerKostenDTO {
  gereedschapsDragerId: string
  van: string | null
  tot: string | null
  posten: DragerKostenPostDTO[]
  totaal: number
  perSoort: Record<string, number>
}

// ─── Template ─────────────────────────────────────────────────────────────────

export interface DragerTemplateRegelDTO {
  id: string
  soort: DragerInhoudSoort
  moduleArtikelId: string | null
  losArtikelId: string | null
  naam: string
  code: string | null
  aantal: number
  retourplichtig: boolean
  volgorde: number
}

export interface DragerTemplateLadeDTO {
  id: string
  nummer: number
  naam: string | null
  opmerking: string | null
  regels: DragerTemplateRegelDTO[]
}

export interface DragerTemplateDTO {
  id: string
  naam: string
  reeks: DragerReeks
  dragerType: string
  aantalLades: number
  controleFrequentie: DragerControleFrequentie
  artikelId: string | null
  artikelNaam: string | null
  omschrijving: string | null
  actief: boolean
  lades: DragerTemplateLadeDTO[]
  /** Aantal karren dat vanaf dit template is aangemaakt. */
  aantalDragers: number
}

/** Preview-regel bij template-doorvoer per kar (nooit stil). */
export interface TemplateDoorvoerPreviewRegelDTO {
  dragerId: string
  nummer: string | null
  naam: string
  /** Wat er zou wijzigen (nieuwe modules/artikelen die het template toevoegt). */
  toevoegingen: string[]
}

// ─── Controle ─────────────────────────────────────────────────────────────────

export interface DragerControleRegelDTO {
  id: string
  inhoudRegelId: string | null
  moduleOnderdeelId: string | null
  omschrijving: string
  ladeNummer: number
  aantal: number
  status: DragerInhoudStatus
  opmerking: string | null
  fotoPath: string | null
}

export interface DragerControleDTO {
  id: string
  dragerId: string
  dragerNummer: string | null
  dragerNaam: string
  status: DragerControleStatus
  gestartOp: string
  gestartDoorNaam: string | null
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  score: number | null
  aantalOk: number | null
  aantalAfwijkend: number | null
  aantalTotaal: number | null
  opmerking: string | null
  regels: DragerControleRegelDTO[]
}

/** Rij in het controles-overzicht. */
export interface DragerControleListRowDTO {
  id: string
  dragerId: string
  dragerNummer: string | null
  dragerNaam: string
  status: DragerControleStatus
  gestartOp: string
  afgerondOp: string | null
  score: number | null
  gestartDoorNaam: string | null
}

// ─── Bestelvoorstel ─────────────────────────────────────────────────────────

/** Eén kandidaat-regel voor het bestelvoorstel (afwijkende controle-regel). */
export interface BestelvoorstelKandidaatDTO {
  controleRegelId: string
  inhoudRegelId: string | null
  omschrijving: string
  ladeNummer: number
  aantal: number
  status: DragerInhoudStatus
  /** Voorgestelde leverancier (module-leverancier of default Pehavo). */
  leverancierNaam: string | null
  leverancierId: string | null
  /** Kan als complete module besteld worden (module-regel met leverancier). */
  kanModuleBestellen: boolean
  /** Kan als los onderdeel besteld worden (los-artikel-koppeling). */
  kanLosBestellen: boolean
  /**
   * FO-321 — het gekoppelde gereedschap-catalogus-item van de bron (module/los),
   * of null. Zo herkent het bestelvoorstel dat er een catalogus-item bestaat met
   * dezelfde voorraad-koppeling.
   */
  catalogusArtikelId: string | null
  /**
   * batch-48 (spec 97 §3.1) — deze afwijking staat al in een open bestelling.
   * De UI toont dat en biedt geen bestel-actie meer aan; het indien-endpoint
   * slaat de regel hoe dan ook over (server-side de waarheid). Null = vrij te
   * bestellen.
   */
  openBestelling: {
    bestellingId: string
    /** Leesbaar kenmerk (BEST-A1B2C3). */
    kenmerk: string
    status: 'CONCEPT' | 'VERZONDEN' | 'GEANNULEERD'
    /** ISO-datum waarop besteld is. */
    besteldOp: string
  } | null
}

/** Uitkomst van het indienen van een bestelvoorstel op een controle. */
export interface BestelvoorstelResultaatDTO {
  bestellingId: string | null
  leverancierNaam: string | null
  aantalBesteld: number
  aantalUitVoorraad: number
  aantalAfgeboekt: number
  /**
   * batch-48 (spec 97 §3.1) — regels die zijn overgeslagen omdat de afwijking
   * al in een open bestelling stond. Nooit stil: de UI meldt dit.
   */
  aantalOvergeslagen: number
}

/** Open bestelregel op een drager (regelniveau IN_BESTELLING). */
export interface DragerBestelRegelDTO {
  id: string
  actie: DragerBestelActie
  aantal: number
  omschrijving: string
  status: DragerInhoudStatus
  aanvraagId: string | null
  besteldOp: string | null
  ontvangenOp: string | null
  gekoppeldTerugOp: string | null
}

// ─── Portal (monteur) — ZONDER kostvelden ────────────────────────────────────

export interface MijnDragerInhoudRegelDTO {
  id: string
  naam: string
  code: string | null
  aantal: number
  status: DragerInhoudStatus
  moduleInhoud: Array<{ omschrijving: string; aantal: number }>
  // FO-321 — "bestel vervanging" vanuit de koffer. catalogusArtikelId = het
  // gekoppelde catalogus-item (bestelflow-preselect); catalogusBestelbaar bepaalt
  // of de portal naar de bestelflow springt of naar de losse aanvraag terugvalt.
  catalogusArtikelId: string | null
  catalogusBestelbaar: boolean
}

export interface MijnDragerLadeDTO {
  id: string
  nummer: number
  naam: string | null
  fotoPath: string | null
  regels: MijnDragerInhoudRegelDTO[]
}

export interface MijnDragerDTO {
  id: string
  uniekItemId: string
  qrToken: string | null
  nummer: string | null
  naam: string
  dragerType: string
  compleetheidPct: number | null
  lades: MijnDragerLadeDTO[]
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

const nummerVoorReeks = z
  .string()
  .regex(/^(KAR|KOF)[0-9]+$/, 'Nummer moet in de KAR- of KOF-reeks vallen (bv. KAR1023)')

/**
 * Koffer/kar uit pakket (2026-08) — één regel-keuze bij het overnemen van de
 * inhoud uit een gereedschapspakket. Ontbreekt een pakket-regel in de lijst,
 * dan geldt de pakket-default (pakket-aantal, niet uit magazijn).
 */
export const createDragerPakketRegelSchema = z
  .object({
    voorraadArtikelId: z.string().min(1),
    /** Aantal dat in de kist komt; 0 = regel weglaten. */
    aantal: z.number().int().min(0).max(999),
    /**
     * Hoeveel van `aantal` uit het magazijn komt (UITGIFTE-mutatie mét dragerId
     * op `magazijnLocatieId`); de rest geldt als aanwezig zonder boeking (bv.
     * meegeleverd met de set). 0 = niets uit magazijn; een deel mag (3 van 5).
     * Alleen voor bulk-artikelen: een traceerbaar artikel koppel je als
     * exemplaar via de kist-inname-flow.
     */
    aantalUitMagazijn: z.number().int().min(0).max(999).default(0),
    /**
     * Verouderde vorm (alles-of-niets-vinkje van de eerste versie). Bewust
     * geaccepteerd in het schema om 'm luid te kunnen weigeren: `z.object`
     * zou 'm anders stil strippen en een oude open tab zou een kist aanmaken
     * zonder ook maar één magazijnboeking.
     */
    uitMagazijn: z.boolean().optional(),
  })
  .superRefine((r, ctx) => {
    if (r.aantalUitMagazijn > r.aantal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Uit magazijn kan niet meer zijn dan het aantal in de kist',
        path: ['aantalUitMagazijn'],
      })
    }
    if (r.uitMagazijn !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Verouderde pagina: "uit magazijn" is nu een aantal. Herlaad de pagina en probeer het opnieuw.',
        path: ['uitMagazijn'],
      })
    }
  })
export type CreateDragerPakketRegelInput = z.infer<typeof createDragerPakketRegelSchema>

/**
 * Kar/koffer aanmaken — vanaf template, uit een gereedschapspakket of leeg.
 * Reeks alleen KAR/KOF (SK = FO-301).
 */
export const createDragerSchema = z
  .object({
    reeks: z.enum(DRAGER_REEKS_AANMAAK),
    dragerType: z.string().min(1).max(80),
    naam: z.string().min(1).max(160),
    /** Het fysieke product-artikel (traceerbaar) waar dit een exemplaar van is. */
    artikelId: z.string().min(1),
    /** Vanaf template: lades + inhoud worden overgenomen. Sluit `pakketId` uit. */
    templateId: z.string().min(1).optional().nullable(),
    /**
     * Koffer/kar uit pakket (2026-08): inhoud overnemen uit een gereedschapspakket
     * (Serviceset/Onderhoudsset). De regels komen als los-artikel-regels in lade 1
     * (of de `standaardLade` van het artikel); de regel van het koffer-product
     * zelf (`artikelId`) wordt overgeslagen. Sluit `templateId` uit.
     */
    pakketId: z.string().min(1).optional().nullable(),
    /** Per pakket-regel: aantal (0 = weglaten) + hoeveel daarvan uit het magazijn komt. Alleen bij `pakketId`. */
    pakketRegels: z.array(createDragerPakketRegelSchema).max(500).optional().nullable(),
    /** Magazijn (bronlocatie) voor de uit-magazijn-boekingen; verplicht zodra één regel `aantalUitMagazijn > 0` heeft. */
    magazijnLocatieId: z.string().min(1).optional().nullable(),
    /** Aantal lades (1-15). Verplicht bij leeg starten; bij een pakket optioneel (default 1). Genegeerd bij templateId. */
    aantalLades: z.number().int().min(1).max(15).optional(),
    controleFrequentie: z.enum(DRAGER_CONTROLE_FREQUENTIE).optional(),
    locatieId: z.string().min(1).optional().nullable(),
    employeeId: z.string().min(1).optional().nullable(),
    voertuigId: z.string().min(1).optional().nullable(),
    projectId: z.string().min(1).optional().nullable(),
    opmerking: z.string().max(2000).optional().nullable(),
    /** Expliciet nummer (naregistreren gestickerde kar); leeg = auto uit de reeks. */
    nummer: nummerVoorReeks.optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (!val.templateId && !val.pakketId && val.aantalLades == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies een template of pakket, of geef het aantal lades op',
        path: ['aantalLades'],
      })
    }
    if (val.templateId && val.pakketId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies óf een template óf een gereedschapspakket als bron, niet allebei',
        path: ['pakketId'],
      })
    }
    const regels = val.pakketRegels ?? []
    if (regels.length > 0 && !val.pakketId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Pakket-regels horen bij een gekozen gereedschapspakket',
        path: ['pakketRegels'],
      })
    }
    const gezien = new Set<string>()
    for (const r of regels) {
      if (gezien.has(r.voorraadArtikelId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Een artikel komt dubbel voor in de pakket-regels',
          path: ['pakketRegels'],
        })
        break
      }
      gezien.add(r.voorraadArtikelId)
    }
    if (regels.some((r) => r.aantalUitMagazijn > 0 && r.aantal > 0) && !val.magazijnLocatieId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies het magazijn waaruit de aangevinkte artikelen worden geboekt',
        path: ['magazijnLocatieId'],
      })
    }
  })
export type CreateDragerInput = z.infer<typeof createDragerSchema>

// ─── Koffer/kar uit pakket (2026-08) — bron-pakketten voor de aanmaak-dialog ──

/** Actief gereedschapspakket als inhoud-bron voor een nieuwe kar/koffer. */
export interface PakketBronDTO {
  id: string
  naam: string
  omschrijving: string | null
  aantalRegels: number
  aantalStuks: number
}

/** Eén pakket-regel met de voorraad-context die de aanmaak-dialog nodig heeft. */
export interface PakketBronRegelDTO {
  voorraadArtikelId: string
  artikelNummer: string
  naam: string
  categorie: string
  maat: string | null
  /** Pakket-aantal (default voor de kist). */
  aantal: number
  /** Traceerbaar artikel: soll-regel wél, "uit magazijn" niet (exemplaar via kist-inname). */
  traceerbaar: boolean
  /** Artikel inactief: niet meer uitgeefbaar, uit-magazijn geblokkeerd. */
  actief: boolean
  standaardLade: number | null
  /** Vrije voorraad (aantal - gereserveerd) op de meegegeven locatie; null zonder `locatieId`. */
  vrijOpLocatie: number | null
  /** Vrije voorraad over alle locaties (bulk) of beschikbare exemplaren (traceerbaar). */
  vrijTotaal: number
}

export interface PakketBronDetailDTO extends PakketBronDTO {
  regels: PakketBronRegelDTO[]
}

/**
 * Bulk-uitrol: per medewerker een drager, of een aantal los op voorraad.
 * Precies EEN van beide; het plafond van 250 houdt de job onder een paar minuten
 * en het resultaat leesbaar. Er wordt niets stil afgekapt.
 */
export const uitrolDragersSchema = z
  .object({
    reeks: z.enum(DRAGER_REEKS_AANMAAK),
    templateId: z.string().min(1),
    artikelId: z.string().min(1),
    employeeIds: z.array(z.string().min(1)).max(250).optional().nullable(),
    aantal: z.number().int().min(1).max(250).optional().nullable(),
    naamPatroon: z.string().trim().min(1).max(160).optional().nullable(),
    locatieId: z.string().min(1).optional().nullable(),
    controleFrequentie: z.enum(DRAGER_CONTROLE_FREQUENTIE).optional(),
    opmerking: z.string().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    const heeftMedewerkers = (val.employeeIds?.length ?? 0) > 0
    const heeftAantal = val.aantal != null
    if (heeftMedewerkers === heeftAantal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies medewerkers OF een aantal, niet allebei en niet geen van beide',
        path: ['aantal'],
      })
    }
  })
export type UitrolDragersInput = z.infer<typeof uitrolDragersSchema>

/** Eén regel in het bulk-uitrol-resultaat (per medewerker, of per los exemplaar). */
export interface UitrolDragerRegelDTO {
  volgnummer: number
  employeeId: string | null
  employeeNaam: string | null
  nummer: string | null
  uniekItemId: string | null
  status: 'AANGEMAAKT' | 'OVERGESLAGEN' | 'FOUT'
  melding: string | null
}

/**
 * Drager-eigenschappen bijwerken (levenscyclus + locatie/voertuig/project + foto's).
 * Medewerker-koppeling loopt bewust NIET hier maar via de bestaande uniek-item-
 * uitgifte/inname (FO-136), zodat het 1:1 UitrustingItem-invariant behouden blijft.
 */
export const updateDragerSchema = z.object({
  naam: z.string().min(1).max(160).optional(),
  dragerType: z.string().min(1).max(80).optional(),
  levensfase: z.enum(DRAGER_LEVENSFASE).optional(),
  controleFrequentie: z.enum(DRAGER_CONTROLE_FREQUENTIE).optional(),
  locatieId: z.string().min(1).optional().nullable(),
  voertuigId: z.string().min(1).optional().nullable(),
  projectId: z.string().min(1).optional().nullable(),
  fotoVoorkantPath: z.string().max(500).optional().nullable(),
  fotoOpenPath: z.string().max(500).optional().nullable(),
  opmerking: z.string().max(2000).optional().nullable(),
})
export type UpdateDragerInput = z.infer<typeof updateDragerSchema>

/** Lade toevoegen aan een drager. */
export const createLadeSchema = z.object({
  naam: z.string().max(120).optional().nullable(),
  opmerking: z.string().max(2000).optional().nullable(),
})
export type CreateLadeInput = z.infer<typeof createLadeSchema>

/** Lade bijwerken (naam/opmerking/foto). */
export const updateLadeSchema = z.object({
  naam: z.string().max(120).optional().nullable(),
  fotoPath: z.string().max(500).optional().nullable(),
  opmerking: z.string().max(2000).optional().nullable(),
})
export type UpdateLadeInput = z.infer<typeof updateLadeSchema>

/**
 * Inhoud-regel toevoegen: precies één van module/los-artikel/los-uniek-item.
 */
export const createInhoudRegelSchema = z
  .object({
    ladeId: z.string().min(1),
    moduleArtikelId: z.string().min(1).optional().nullable(),
    losArtikelId: z.string().min(1).optional().nullable(),
    losUniekItemId: z.string().min(1).optional().nullable(),
    aantal: z.number().int().min(1).max(9999).optional().default(1),
    retourplichtig: z.boolean().optional().default(false),
    opmerking: z.string().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    const refs = [val.moduleArtikelId, val.losArtikelId, val.losUniekItemId].filter(Boolean)
    if (refs.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies precies één: een module, een los artikel of een los uniek item',
        path: ['moduleArtikelId'],
      })
    }
  })
export type CreateInhoudRegelInput = z.infer<typeof createInhoudRegelSchema>

/** Inhoud-regel bijwerken (aantal/status/retour/opmerking) of verplaatsen naar lade. */
export const updateInhoudRegelSchema = z.object({
  aantal: z.number().int().min(1).max(9999).optional(),
  status: z.enum(DRAGER_INHOUD_STATUS).optional(),
  retourplichtig: z.boolean().optional(),
  opmerking: z.string().max(2000).optional().nullable(),
  /** Verplaats naar een andere lade van dezelfde drager. */
  naarLadeId: z.string().min(1).optional(),
})
export type UpdateInhoudRegelInput = z.infer<typeof updateInhoudRegelSchema>

// ─── Kist-inname (2026-07) — drager als inname-bestemming ────────────────────

/**
 * Picker-rij voor de inname-doel-picker (GET /karren/dragers-picker).
 * plekLabel is de afgeleide plek van de kist: "bij {houder}" / locatienaam /
 * "in bus {kenteken}". Lades reizen mee zodat de lade-keuze geen extra call
 * kost (max 15 per drager).
 */
export interface DragerPickerRowDTO {
  id: string
  uniekItemId: string
  /** Asset-tag, bv. "KOF1001"; null zolang het exemplaar geen tag heeft. */
  nummer: string | null
  naam: string
  reeks: DragerReeks
  dragerType: string
  levensfase: DragerLevensfase
  plekLabel: string
  lades: Array<{ id: string; nummer: number; naam: string | null }>
}

/** Query voor GET /karren/dragers-picker (server-side search, bounded). */
export const dragerPickerQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().positive().max(50).default(20),
})
export type DragerPickerQuery = z.infer<typeof dragerPickerQuerySchema>

/**
 * Kist-inhoud terug naar voorraad (POST /karren/inhoud-regels/:id/naar-voorraad).
 * Los-uniek-regel: item → OP_VOORRAAD op de locatie (aantal genegeerd).
 * Los-artikel-regel: `aantal` verplicht; boekt een RETOUR-mutatie zonder
 * medewerker (dragerId gevuld) en verlaagt de regel — dicht het gat dat
 * kist-aantallen stil verlaagd konden worden zonder terugboeking.
 */
export const kistNaarVoorraadSchema = z.object({
  locatieId: z.string().min(1),
  aantal: z.number().int().min(1).max(9999).optional().nullable(),
  opmerking: z.string().max(2000).optional().nullable(),
})
export type KistNaarVoorraadInput = z.infer<typeof kistNaarVoorraadSchema>

/**
 * Uniek item vanuit de kist uitgeven aan een medewerker
 * (POST /karren/inhoud-regels/:id/uitgeven, los-uniek-regel).
 */
export const uitgevenUitDragerSchema = z.object({
  employeeId: z.string().min(1),
  reden: z.string().min(1).max(500),
  verwachteRetour: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
})
export type UitgevenUitDragerInput = z.infer<typeof uitgevenUitDragerSchema>

// ─── Module-catalogus schemas ────────────────────────────────────────────────

export const createModuleSchema = z.object({
  naam: z.string().min(1).max(160),
  moduleMerk: z.string().min(1).max(80),
  moduleCode: z.string().min(1).max(80),
  categorie: z.string().min(1).max(60).optional(),
  standaardLade: z.number().int().min(1).max(15).optional().nullable(),
  leverancierId: z.string().min(1).optional().nullable(),
  leverancierArtNr: z.string().max(120).optional().nullable(),
  prijs: z.number().min(0).max(1_000_000).optional().nullable(),
  // Schrijft VoorraadArtikel.fotoUrl (LAYIN-module): zelfde klem als het
  // artikel-schema — vrij tekst zou via de foto-GET een blob-read-gat openen.
  fotoUrl: fotoBronSchema.optional().nullable(),
})
export type CreateModuleInput = z.infer<typeof createModuleSchema>

export const updateModuleSchema = z.object({
  naam: z.string().min(1).max(160).optional(),
  moduleMerk: z.string().min(1).max(80).optional(),
  moduleCode: z.string().min(1).max(80).optional(),
  categorie: z.string().min(1).max(60).optional(),
  standaardLade: z.number().int().min(1).max(15).optional().nullable(),
  leverancierId: z.string().min(1).optional().nullable(),
  leverancierArtNr: z.string().max(120).optional().nullable(),
  prijs: z.number().min(0).max(1_000_000).optional().nullable(),
  // Zelfde klem als createModuleSchema (blob-read-gat, zie fotoBronSchema).
  fotoUrl: fotoBronSchema.optional().nullable(),
  actief: z.boolean().optional(),
})
export type UpdateModuleInput = z.infer<typeof updateModuleSchema>

export const createModuleOnderdeelSchema = z.object({
  omschrijving: z.string().min(1).max(200),
  aantal: z.number().int().min(1).max(9999).optional().default(1),
  losArtikelId: z.string().min(1).optional().nullable(),
})
export type CreateModuleOnderdeelInput = z.infer<typeof createModuleOnderdeelSchema>

export const updateModuleOnderdeelSchema = z.object({
  omschrijving: z.string().min(1).max(200).optional(),
  aantal: z.number().int().min(1).max(9999).optional(),
  losArtikelId: z.string().min(1).optional().nullable(),
})
export type UpdateModuleOnderdeelInput = z.infer<typeof updateModuleOnderdeelSchema>

// ─── Template schemas ─────────────────────────────────────────────────────────

const templateRegelInput = z
  .object({
    moduleArtikelId: z.string().min(1).optional().nullable(),
    losArtikelId: z.string().min(1).optional().nullable(),
    aantal: z.number().int().min(1).max(9999).optional().default(1),
    retourplichtig: z.boolean().optional().default(false),
  })
  .superRefine((val, ctx) => {
    const refs = [val.moduleArtikelId, val.losArtikelId].filter(Boolean)
    if (refs.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies precies één: een module of een los artikel',
        path: ['moduleArtikelId'],
      })
    }
  })

const templateLadeInput = z.object({
  nummer: z.number().int().min(1).max(15),
  naam: z.string().max(120).optional().nullable(),
  opmerking: z.string().max(2000).optional().nullable(),
  regels: z.array(templateRegelInput).max(200).optional().default([]),
})

export const createTemplateSchema = z.object({
  naam: z.string().min(1).max(160),
  reeks: z.enum(DRAGER_REEKS),
  dragerType: z.string().min(1).max(80),
  controleFrequentie: z.enum(DRAGER_CONTROLE_FREQUENTIE).optional(),
  artikelId: z.string().min(1).optional().nullable(),
  omschrijving: z.string().max(2000).optional().nullable(),
  lades: z.array(templateLadeInput).min(1).max(15),
})
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>

export const updateTemplateSchema = z.object({
  naam: z.string().min(1).max(160).optional(),
  dragerType: z.string().min(1).max(80).optional(),
  controleFrequentie: z.enum(DRAGER_CONTROLE_FREQUENTIE).optional(),
  artikelId: z.string().min(1).optional().nullable(),
  omschrijving: z.string().max(2000).optional().nullable(),
  actief: z.boolean().optional(),
  /** Volledige lades-set vervangen (optioneel; laat weg om alleen kop-velden te wijzigen). */
  lades: z.array(templateLadeInput).min(1).max(15).optional(),
})
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>

/** Template doorvoeren naar bestaande karren (na preview-bevestiging). */
export const templateDoorvoerSchema = z.object({
  dragerIds: z.array(z.string().min(1)).min(1).max(500),
})
export type TemplateDoorvoerInput = z.infer<typeof templateDoorvoerSchema>

// ─── Controle schemas ─────────────────────────────────────────────────────────

/** Eén regel-status zetten tijdens een lopende controle. */
export const setControleRegelSchema = z.object({
  status: z.enum(DRAGER_INHOUD_STATUS),
  opmerking: z.string().max(2000).optional().nullable(),
  fotoPath: z.string().max(500).optional().nullable(),
})
export type SetControleRegelInput = z.infer<typeof setControleRegelSchema>

/** "Alles aanwezig" per lade (of hele controle als ladeNummer leeg). */
export const controleSnelknopSchema = z.object({
  ladeNummer: z.number().int().min(1).max(15).optional().nullable(),
})
export type ControleSnelknopInput = z.infer<typeof controleSnelknopSchema>

export const afrondControleSchema = z.object({
  opmerking: z.string().max(2000).optional().nullable(),
})
export type AfrondControleInput = z.infer<typeof afrondControleSchema>

// ─── Bestelvoorstel schemas ──────────────────────────────────────────────────

const bestelRegelKeuze = z
  .object({
    controleRegelId: z.string().min(1),
    actie: z.enum(DRAGER_BESTEL_ACTIE),
    aantal: z.number().int().min(1).max(9999).optional().default(1),
    /** Verplicht bij AFBOEKEN. */
    afboekReden: z.string().max(2000).optional().nullable(),
    /** Bij UIT_VOORRAAD: uit welke locatie leveren. */
    locatieId: z.string().min(1).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.actie === 'AFBOEKEN' && !val.afboekReden?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Afboeken vereist een reden',
        path: ['afboekReden'],
      })
    }
  })

/** Bestelvoorstel indienen: per afwijkende regel een actie. Bundelt per leverancier. */
export const bestelvoorstelSchema = z.object({
  controleId: z.string().min(1),
  regels: z.array(bestelRegelKeuze).min(1).max(500),
})
export type BestelvoorstelInput = z.infer<typeof bestelvoorstelSchema>

/** Ontvangst-terugkoppeling: een bestelregel is geleverd → koppel terug naar de kar. */
export const ontvangstKoppelSchema = z.object({
  /** Nieuwe status van de kar-regel na ontvangst (default AANWEZIG). */
  status: z.enum(['AANWEZIG', 'VERVANGEN']).optional().default('AANWEZIG'),
})
export type OntvangstKoppelInput = z.infer<typeof ontvangstKoppelSchema>

// ─── Portal-melding schema ────────────────────────────────────────────────────

/** Monteur meldt een onderdeel ontbrekend/kapot (→ notificatie magazijn). */
export const dragerMeldingSchema = z.object({
  inhoudRegelId: z.string().min(1),
  soort: z.enum(['ONTBREEKT', 'KAPOT']),
  opmerking: z.string().max(2000).optional().nullable(),
})
export type DragerMeldingInput = z.infer<typeof dragerMeldingSchema>
